import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import LockIcon from '@mui/icons-material/Lock';
import { IconButton, Menu, MenuItem } from "@mui/material";
import SidebarItems from "./SidebarItems";
import "./HomeScreenSidebar.css";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import SidebarTooltip from "./elements/SidebarTooltip";
import SidebarDrawer from "./elements/SidebarDrawer";
import NiramayaAppBar from "./elements/NiramayaAppBar";
import { GetDrawerWidth } from "../utils/applicationutils";
import NiramayaDrawerHeader from "./elements/NiramayaDrawerHeader";
import ConfigurationConstants from "../utils/configurationconstants";
import { currentConfiguration } from "../../service/memory/recoil/configurationstore";
import { useRecoilState, useRecoilValue } from "recoil";
import { useGetRole } from "../generic/hooks/useGetRole";
import { RoleToLinkMapper } from "../utils/UrlUtils";
import { Avatar, Badge, Tooltip } from "antd";
import { TeamOutlined, UserOutlined } from "@ant-design/icons";
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import { themeData } from "../../service/memory/recoil/theme";
import NotificationIcon from "./elements/NotificationIcon";
import InstallationIcon from "../landingpage/install/InstallationIcon";
import OfficeSearchAndSelect from "../carecoordinator/requestforvolunteeringservice/components/OfficeSearchAndSelect";
import SmartIcon from "../../components/generic/genericIcon/SmartIcon";
import { performPostRequest } from "../../service/network/NetworkService";
import { getApiUrl } from "../../service/network/UrlMapper";
import IntentType from "../../intentexecutor/utils/IntentType";
import { useReadMemory } from "../generic/hooks/useReadMemory";
import MemoryType from "../../service/memory/enum/MemoryType";
import CustomModal from "../../components/generic/custommodal/CustomModal";
import OrganizationList from "./OrganizationList";
import WebAuthnSwitch from "./elements/WebAuthnSwitch";
import ChangePinModal from "./ChangePinModal";
import HelpDesk from "./HelpDesk";
import Banner from "./Banner";
import Notification from "./notification/Notification";
import { isOrganizationChanged, teamList } from "../../components/recoil/teamsRecoil";
import { totalChatUnreadCountState } from "../chat/state/chatState";
import { useGetOrgIdentifier } from "../generic/hooks/useGetOrgIdentifier";

const drawerWidth = GetDrawerWidth();

const HomeScreenSidebar = (props) => {
	const { window : windowObject } = props;
	const [open, setOpen] = useState(false); //state for web view
	const [mobileOpen, setMobileOpen] = useState(false); //state for mobile view
	const [allowedSidebarItems, setAllowedSidebarItems] = useState([]);
	const configuration = useRecoilValue(currentConfiguration);
	const role = useGetRole();
	const { pathname } = useLocation();
	const userDetails = useReadMemory(MemoryType.USER);
	const [selectedIndex, setSelectedIndex] = useState(sessionStorage.getItem("sidebarItemSelected"));
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = React.useState(null);
	const themeValue = useRecoilValue(themeData);
	const [teams, setTeams] = useRecoilState(teamList);
	const isChanged = useRecoilValue(isOrganizationChanged);
	const [showNotificationIcon,setShowNotificationIcon]= useState(false)
	const [showTeamsSelect, setShowTeamsSelect]= useState(false)
	const [isAdmin, setIsAdmin] = useState(false);
	const [isModalVisible, setModalVisible] = useState(false);
	const [isPinModalVisible, setPinModalVisible] = useState(false);
	const [coordinatorIdentifiers, setCoordinatorIdentifiers] = useState(null);
	const organizationIdentifier = useGetOrgIdentifier();
	const chatUnreadTotal = useRecoilValue(totalChatUnreadCountState);
	const teamsOptions = useMemo(() => {
		return showTeamsSelect ? userDetails?.teams
			?.filter((team) => team?.organizationIdentifier === userDetails?.organizationIdentifier)
			?.map((team) => ({
				...team,
				label: team?.name,
				value: team?.id,
			})) : []
	}, [showTeamsSelect, userDetails?.organizationIdentifier, userDetails?.teams, isChanged])

	const container = windowObject !== undefined ? () => windowObject().document.body : undefined;

	const getProfileMenus = () => {
		const organizationProfileItemConfiguration = configuration[ConfigurationConstants.FEATUREACCESS_MENUHEADER_ORGANIZATION_PROFILE];
		const coordinatorProfileItemConfiguration = configuration[ConfigurationConstants.FEATUREACCESS_MENUHEADER_COORDINATOR_PROFILE];
		const volunteerProfileItemConfiguration = configuration[ConfigurationConstants.FEATUREACCESS_MENUHEADER_VOLUNTEER_PROFILE];
		const patientProfileItemConfiguration = configuration[ConfigurationConstants.FEATUREACCESS_MENUHEADER_PATIENT_PROFILE];
		const profileMenu = [];
		const hasCoordinatorAccess =
			coordinatorIdentifiers === null ||
			coordinatorIdentifiers.includes(userDetails.userIdentifier);

		if (coordinatorProfileItemConfiguration?.visibility) {
			profileMenu.push({
				name: "Coordinator Profile",
				icon: <UserOutlined />,
				role: "Volunteer Coordinator",
			});
		}
		if (organizationProfileItemConfiguration?.visibility && hasCoordinatorAccess) {
			profileMenu.push({
				name: "Organization Profile",
				icon: <TeamOutlined />,
				role: "Volunteer Coordinator",
			});
		}
		if (volunteerProfileItemConfiguration?.visibility) {
			profileMenu.push({
				name: "Volunteer Profile",
				icon: <UserOutlined />,
				role: "Volunteer",
			});
		}
		if (patientProfileItemConfiguration?.visibility) {
			profileMenu.push({
				name: "Patient Profile",
				icon: <UserOutlined />,
				role: "Patient",
			});
		}
		return profileMenu;
	};

	const handleMenu = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuItemClick = (menuItem) => {
		const hasCoordinatorAccess =
			coordinatorIdentifiers === null ||
			coordinatorIdentifiers.includes(userDetails.userIdentifier);

		if (menuItem === "Organization Profile" && role === "Volunteer Coordinator" && hasCoordinatorAccess) {
			navigate("/carecoordinator/organization-settings");
		} else if (menuItem === "Coordinator Profile" && role === "Volunteer Coordinator") {
			navigate("/carecoordinator/cooordinator-profile");
		} else if (menuItem === "Volunteer Profile" && role === "Volunteer") {
			navigate("/careprovider/volunteer-profile");
		} else if (menuItem === "Patient Profile" && role === "Patient") {
			navigate("/careconsumer/patient-profile");
		} else {
			return;
		}
		handleClose();
	};

	const handleSelectOrganization = () => {
		setModalVisible(true)
		handleClose()
	}

	const handlePinChange = () => {
		setPinModalVisible(true)
		handleClose()
	}

	//Toggle functions for web view
	const handleDrawer = () => {
		setOpen(() => !open);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	// Toggle function in mobile view
	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	useEffect(() => {
		if (selectedIndex === "Sign Out") sessionStorage.clear();
	}, [selectedIndex]);

	useEffect(() => {
		for (let item = 0; item < SidebarItems?.length; item++) {
			if (pathname === RoleToLinkMapper(role) + SidebarItems[item].menuTextVariable) {
				setSelectedIndex(SidebarItems[item].menuTitle);
				sessionStorage.setItem("sidebarItemSelected", SidebarItems[item].menuTitle);
			}
		}
	}, [pathname]);

	useEffect(() => {
		const getAllowedItems = async () => {
			const allowedItems = [];
			for (const sidebarItem of SidebarItems) {
				const configurationKey = ConfigurationConstants.FEATUREACCESS_SIDEBAR_ITEMS + "." + sidebarItem.menuTextVariable.toLowerCase();
				const currentSidebarItemConfiguration = configuration[configurationKey];
				if (currentSidebarItemConfiguration?.visibility) {
					sidebarItem.visibility = true;
					allowedItems.push(sidebarItem);
				}
			}
			setAllowedSidebarItems(allowedItems);
		};
		getAllowedItems();
	}, [configuration]);

	useEffect(() => {
		const configurationAllowNotificationicon =
			configuration[ConfigurationConstants.FEATUREACCESS_NOTIFICATION_ICON];
		setShowNotificationIcon(configurationAllowNotificationicon?.visibility);
		const configurationAllowTeamSelect =
			configuration[ConfigurationConstants.FEATUREACCESS_MENUHEADER_TEAMSELECT];
		setShowTeamsSelect(configurationAllowTeamSelect?.visibility);
	}, [configuration]);

	useEffect(() => {
		const getAdminIdentifiers = async () => {
			try {
				const payload = {
					intent: "get_configuration",
					payload: {
						configurationKey: ["niramaya.admin.identifiers"],
					},
					context: {},
				};
				const result = await performPostRequest(
					getApiUrl()[IntentType.GET_CONFIGURATION],
					JSON.stringify(payload)
				);
				const response = await result.json();
				const identifiers = JSON.parse(response[0].configurationValue);
				const isAdmin = identifiers?.includes(userDetails?.userIdentifier)
				setIsAdmin(isAdmin);
			} catch (err) {
				setIsAdmin(false);
			}
		};

		getAdminIdentifiers()
	}, [userDetails?.userIdentifier])

	useEffect(() => {
		const getCoordinatorIdentifiers = async () => {
			try {
				const payload = {
					intent: "get_configuration",
					payload: {
						configurationKey: ["niramaya.featureaccess.coordinator.profile.access"],
					},
					context: { organizationIdentifier },
				};
				const result = await performPostRequest(getApiUrl()[IntentType.GET_CONFIGURATION], JSON.stringify(payload));
				const response = await result.json();
				const identifiers = JSON.parse(response[0].configurationValue);
				setCoordinatorIdentifiers(identifiers);
			} catch (err) {
				setCoordinatorIdentifiers(null);
			}
		};

		getCoordinatorIdentifiers();
	}, [organizationIdentifier]);

	useEffect(() => {
		setTeams(teams?.length ? teams : teamsOptions);
	}, [setTeams, teams, teamsOptions]);

	const drawer = (
		<div
			className="homeScreen_sidebar"
		>
			<List>
				{allowedSidebarItems.map((item) =>
					item.visibility ? (
						<>
							{
								item.menuTextVariable === "helpdesk" ? (
									<HelpDesk open={open} mobileOpen={mobileOpen} />
								) : (
									<Link to={item.menuTextVariable} key={item.menuTextVariable} style={{ textDecoration: "none", color: "inherit" }}>
										<ListItem disablePadding>
											<SidebarTooltip title={item.menuTitle} placement="right" arrow>
												<ListItemButton
													selected={pathname === RoleToLinkMapper(role) + item.menuTextVariable || item.menuTitle === selectedIndex}
													onClick={() => {
														setSelectedIndex(item.menuTitle);
														sessionStorage.setItem("sidebarItemSelected", item.menuTitle);
														if (mobileOpen) {
															handleDrawerToggle();
														}
													}}
													sx={{
														minHeight: 48,
														justifyContent: mobileOpen || open ? "initial" : "center",
														px: 2.5,
														"&.Mui-selected": {
															backgroundColor: "var(--main-theme-color) !important",
														},
														"&.Mui-selected:hover": {
															backgroundColor: "var(--main-theme-color) !important",
														},
													}}
												>
													<ListItemIcon
														edge="start"
														sx={{
															minWidth: 0,
															mr: mobileOpen || open ? 2 : "auto",
															justifyContent: "center",
															ml: 0.4,
														}}
													>
														{item.menuTextVariable === "communication" ? (
															<Badge
																count={chatUnreadTotal}
																size="small"
																offset={[6, -2]}
																overflowCount={99}
															>
																<SmartIcon
																	color={
																		selectedIndex === item.menuTitle
																			? "hsla(0,0%,100%)"
																			: "var(--secondary-theme-color)"
																	}
																	iconKey={item.iconName}
																	width={20}
																	height={20}
																/>
															</Badge>
														) : (
															<SmartIcon
																color={
																	selectedIndex === item.menuTitle
																		? "hsla(0,0%,100%)"
																		: "var(--secondary-theme-color)"
																}
																iconKey={item.iconName}
																width={20}
																height={20}
															/>
														)}
													</ListItemIcon>
													<ListItemText
														primary={item.menuTitle}
														sx={{
															opacity: mobileOpen || open ? 1 : 0,
															color: selectedIndex === item.menuTitle ? "var(--menu-text-selected-color)" : "",
															marginBottom: 0
														}}
													/>
												</ListItemButton>
											</SidebarTooltip>
										</ListItem>
									</Link>
								)
							}
						</>
					) : null
				)}
			</List>
		</div>
	);

	return (
		<Box sx={{ display: "flex" }}>
			<CssBaseline />
			<NiramayaAppBar
				position="fixed"
				sx={{
					backgroundColor: "#ffffff",
					color: "#001529",
					zIndex: (theme) => theme.zIndex.drawer + 1,
					boxShadow: `0 1px 4px rgba(0,21,41,.08)`,
				}}
			>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<Toolbar>
						<IconButton
							color="inherit"
							aria-label="open drawer"
							edge="start"
							onClick={handleDrawerToggle}
							sx={{
								mr: 2,
								display: { sm: "none" },
							}}
						>
							<MenuOutlinedIcon style={{ color: "#001529" }} />
						</IconButton>
						<IconButton
							onClick={handleDrawer}
							edge="start"
							sx={{
								mr: 2,
								display: { xs: "none", sm: "block" },
							}}
						>
							<MenuOutlinedIcon style={{ color: "#001529" }} />
						</IconButton>
						<Typography variant="h5" noWrap component="div" className="cursor-pointer" onClick={() => navigate(RoleToLinkMapper(role) + "homepage")}>
							<img style={{ maxWidth: 160, maxHeight: 60, objectFit: "contain" }} src={themeValue?.assets?.logo} alt="Primary logo" />
						</Typography>
					</Toolbar>
					<div style={{ display: "inline-flex", marginRight: "1em" }}>
						{
							showNotificationIcon && <NotificationIcon/>
						}
						<Tooltip title="Install App" placement="bottom">
							<>
								<InstallationIcon />
							</>
						</Tooltip>

						<Notification />
						
						<Tooltip title="Profile" placement="bottom">
							<IconButton style={{ padding: 4 }} size="large" aria-label="account of current user" aria-controls="menu-appbar" aria-haspopup="true" onClick={handleMenu} color="inherit">
								<Avatar
									style={{
										backgroundColor: "var(--main-theme-color)",
									}}
									icon={<UserOutlined />}
								/>
							</IconButton>
						</Tooltip>
						<Menu
							anchorOrigin={{
								vertical: "top",
								horizontal: "right",
							}}
							sx={{ mt: "45px" }}
							transformOrigin={{
								vertical: "top",
								horizontal: "right",
							}}
							slotProps={{
								paper: {
								  sx: {
									overflow: 'visible',
									maxHeight: 'none',
								  },
								},
							  }}
							open={Boolean(anchorEl)}
							onClose={handleClose}
						>
							{getProfileMenus().map((items) => (
								<MenuItem key={items.name} onClick={() => handleMenuItemClick(items.name)}>
									<Avatar icon={items.icon} style={{ marginRight: "0.5em", backgroundColor:'var(--main-theme-color)'}} />
									<Typography textAlign="center">{items.name}</Typography>
								</MenuItem>
							))}
							{
								isAdmin && (
									<MenuItem onClick={handleSelectOrganization}>
										<Avatar icon={<DomainAddIcon />} style={{ marginRight: "0.5em", backgroundColor: 'var(--main-theme-color)', display:"flex", justifyContent:"center", alignItems:"center" }} />
										<Typography textAlign="center">Select Organization</Typography>
									</MenuItem>
								)
							}
							<MenuItem>
								<WebAuthnSwitch />
							</MenuItem>
							<MenuItem onClick={handlePinChange}>
								<Avatar icon={<LockIcon />} style={{ marginRight: "0.5em", backgroundColor: 'var(--main-theme-color)', display: "flex", justifyContent: "center", alignItems: "center" }} />
								<Typography textAlign="center">Update PIN</Typography>
							</MenuItem>
							{showTeamsSelect && Boolean(teamsOptions.length) && (
								<MenuItem>
									<OfficeSearchAndSelect teamsOptions={teamsOptions} />
								</MenuItem>
							)}
						</Menu>
					</div>
				</div>
			</NiramayaAppBar>

			{/** Drawer for mobile view */}
			<Drawer
				container={container}
				variant="temporary"
				open={mobileOpen}
				onClose={handleDrawerToggle}
				ModalProps={{
					keepMounted: true, // Better open performance on mobile.
				}}
				sx={{
					display: { xs: "block", md: "none" },
					"& .MuiDrawer-paper": {
						boxSizing: "border-box",
						width: drawerWidth,
					},
				}}
			>
				<img src={themeValue?.assets?.logo} style={{ maxWidth: 160, maxHeight: 60, objectFit: "contain" }} alt="Primary logo" onClick={() => navigate(RoleToLinkMapper(role) + "homepage")} className="homescreen-logo-mobile-view cursor-pointer"/>
				{drawer}
			</Drawer>

			{/* Drawer for web view */}
			<SidebarDrawer
				variant="permanent"
				sx={{
					display: {
						xs: "none",
						sm: "block",
					},
				}}
				open={open}
			>
				<NiramayaDrawerHeader />
				<Divider />
				{drawer}
			</SidebarDrawer>

			<Box
				component="main"
				sx={{
					flexGrow: 1,
					p: 0,
					// width: { sm: `calc(100% - ${drawerWidth}px)` },
					backgroundColor: "var(--bg-color)",
					height: "100%",
				}}
			>
				<Toolbar />
				<Banner />
				<Outlet />
				{/* Is used for rendering the child and when press back button of browser it will get redirected */}
			</Box>
			{isModalVisible && (
				<CustomModal
					closable={true}
					isModalOpen={isModalVisible}
					closeModal={setModalVisible}
					footer={[]}
					title="Select Organization"
				>
					<OrganizationList />
				</CustomModal>
			)}
			{isPinModalVisible && (
				<ChangePinModal isPinModalVisible={isPinModalVisible} setPinModalVisible={setPinModalVisible} />
			)}
		</Box>
	);
};

export default HomeScreenSidebar;
